/**
 * AdminSupportPanel - Admin Support Management
 * =============================================
 * Admin interface for managing user support requests.
 * Features: dashboard stats, request list, respond to users, status management.
 * 
 * @version 1.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  MessageCircle, AlertCircle, Clock, CheckCircle2, Users, 
  ChevronRight, Send, Loader2, RefreshCw, Filter, Search,
  ArrowLeft, User, Mail, Calendar, Tag, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Status configuration
const STATUS_CONFIG = {
  new: { label: 'Nuovo', color: 'bg-[var(--gold)]', icon: AlertCircle },
  in_progress: { label: 'In lavorazione', color: 'bg-yellow-500', icon: Clock },
  awaiting_user: { label: 'Attesa risposta', color: 'bg-[#B08B5C]', icon: MessageCircle },
  resolved: { label: 'Risolto', color: 'bg-green-500', icon: CheckCircle2 },
  closed: { label: 'Chiuso', color: 'bg-gray-500', icon: X }
};

const PRIORITY_CONFIG = {
  low: { label: 'Bassa', color: 'text-gray-400' },
  normal: { label: 'Normale', color: 'text-white' },
  high: { label: 'Alta', color: 'text-orange-400' },
  urgent: { label: 'Urgente', color: 'text-red-400' }
};

const CONTEXT_LABELS = {
  image_editor: 'Editor Immagini',
  visual_search: 'Ricerca Visiva',
  materials: 'Materiali',
  billing: 'Fatturazione',
  general: 'Generale'
};

/**
 * Dashboard Stats Card
 */
const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-gray-400 text-sm">{label}</span>
      <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

/**
 * Request Detail Modal
 */
const RequestDetailModal = ({ request, responses, onClose, onRespond, onUpdateStatus, adminId }) => {
  const [replyMessage, setReplyMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [newStatus, setNewStatus] = useState(request.status);
  
  const handleRespond = async () => {
    if (!replyMessage.trim()) return;
    
    setIsSending(true);
    try {
      await onRespond(request.id, replyMessage);
      setReplyMessage('');
      toast.success('Risposta inviata');
    } catch (error) {
      toast.error('Errore nell\'invio');
    } finally {
      setIsSending(false);
    }
  };
  
  const handleStatusChange = async (status) => {
    try {
      await onUpdateStatus(request.id, status);
      setNewStatus(status);
      toast.success('Stato aggiornato');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };
  
  const statusConfig = STATUS_CONFIG[request.status];
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0a0a0a] rounded-2xl border border-[#1a1a1a] w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#1a1a1a]">
          <div>
            <h3 className="text-xl font-semibold text-white mb-1">{request.subject}</h3>
            <div className="flex items-center gap-3 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {request.user_name || request.user_email}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(request.created_at).toLocaleDateString('it-IT')}
              </span>
              <span className="flex items-center gap-1">
                <Tag className="w-4 h-4" />
                {CONTEXT_LABELS[request.context]}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Status bar */}
        <div className="flex items-center gap-2 px-6 py-3 bg-[#121212] border-b border-[#1a1a1a]">
          <span className="text-sm text-gray-400">Stato:</span>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <button
              key={key}
              onClick={() => handleStatusChange(key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                newStatus === key 
                  ? `${config.color} text-white` 
                  : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#2a2a2a]'
              }`}
            >
              {config.label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Original message */}
          <div className="bg-[#121212] rounded-xl p-4 border border-[#1a1a1a]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-[#B08B5C]/20 flex items-center justify-center">
                <User className="w-4 h-4 text-[#B08B5C]" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">{request.user_name || 'Utente'}</p>
                <p className="text-gray-500 text-xs">{request.user_email}</p>
              </div>
            </div>
            <p className="text-gray-300 whitespace-pre-wrap">{request.message}</p>
          </div>
          
          {/* Responses */}
          {responses.map((response) => (
            <div
              key={response.id}
              className={`p-4 rounded-xl ${
                response.responder_type === 'admin'
                  ? 'bg-[#B08B5C]/10 border border-[#B08B5C]/30 ml-8'
                  : 'bg-[#121212] border border-[#1a1a1a] mr-8'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${
                  response.responder_type === 'admin' ? 'text-[#B08B5C]' : 'text-gray-300'
                }`}>
                  {response.responder_type === 'admin' ? 'Admin' : 'Utente'}
                </span>
                <span className="text-gray-500 text-xs">
                  {new Date(response.created_at).toLocaleString('it-IT')}
                </span>
              </div>
              <p className="text-gray-300 whitespace-pre-wrap">{response.message}</p>
            </div>
          ))}
        </div>
        
        {/* Reply input */}
        <div className="p-4 border-t border-[#1a1a1a] bg-[#121212]">
          <div className="flex gap-3">
            <textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              rows={2}
              placeholder="Scrivi una risposta..."
              className="flex-1 px-4 py-2 rounded-lg bg-[#0a0a0a] border border-[#2a2a2a] text-white placeholder-gray-600 focus:border-[#B08B5C] focus:outline-none resize-none"
            />
            <Button
              onClick={handleRespond}
              disabled={isSending || !replyMessage.trim()}
              className="bg-[#B08B5C] text-black hover:bg-[#C9A46E] self-end"
            >
              {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Main Admin Support Panel
 */
const AdminSupportPanel = ({ adminUser }) => {
  const [stats, setStats] = useState({
    total_requests: 0,
    new_requests: 0,
    in_progress: 0,
    awaiting_user: 0,
    resolved: 0,
    urgent_count: 0
  });
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responses, setResponses] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/support/admin/dashboard`);
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);
  
  // Fetch requests
  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      let url = `${API}/api/support/admin/requests?limit=100`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);
  
  useEffect(() => {
    fetchStats();
    fetchRequests();
  }, [fetchStats, fetchRequests]);
  
  // Fetch request detail
  const fetchRequestDetail = async (request) => {
    try {
      // For admin, we fetch all responses directly
      const response = await fetch(`${API}/api/support/requests/${request.id}?user_id=${request.user_id}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedRequest(data.request);
        setResponses(data.responses || []);
      }
    } catch (error) {
      // Fallback - just open with current data
      setSelectedRequest(request);
      setResponses([]);
    }
  };
  
  // Admin respond
  const handleRespond = async (requestId, message) => {
    const adminId = adminUser?.id || 'admin';
    
    const response = await fetch(
      `${API}/api/support/admin/requests/${requestId}/respond?admin_id=${adminId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: requestId, message })
      }
    );
    
    const data = await response.json();
    
    if (data.success) {
      // Refresh responses
      fetchRequestDetail(selectedRequest);
      fetchStats();
    } else {
      throw new Error(data.detail || 'Error');
    }
  };
  
  // Update status
  const handleUpdateStatus = async (requestId, status) => {
    const response = await fetch(
      `${API}/api/support/admin/requests/${requestId}?status=${status}`,
      { method: 'PATCH' }
    );
    
    const data = await response.json();
    
    if (data.success) {
      fetchRequests();
      fetchStats();
      if (selectedRequest) {
        setSelectedRequest({ ...selectedRequest, status });
      }
    } else {
      throw new Error(data.detail || 'Error');
    }
  };
  
  // Filter requests by search term
  const filteredRequests = requests.filter(req => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      req.subject?.toLowerCase().includes(term) ||
      req.user_email?.toLowerCase().includes(term) ||
      req.user_name?.toLowerCase().includes(term)
    );
  });
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <MessageCircle className="w-7 h-7 text-[#B08B5C]" />
            Gestione Supporto
          </h2>
          <p className="text-gray-400 mt-1">Gestisci le richieste di assistenza degli utenti</p>
        </div>
        <Button
          onClick={() => { fetchStats(); fetchRequests(); }}
          variant="outline"
          className="border-[#2a2a2a] text-gray-400"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Aggiorna
        </Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Totali" value={stats.total_requests} icon={MessageCircle} color="bg-[#1a1a1a]" />
        <StatCard label="Nuove" value={stats.new_requests} icon={AlertCircle} color="bg-[var(--gold)]/20" />
        <StatCard label="In lavorazione" value={stats.in_progress} icon={Clock} color="bg-yellow-500/20" />
        <StatCard label="Attesa risposta" value={stats.awaiting_user} icon={MessageCircle} color="bg-[#B08B5C]/20" />
        <StatCard label="Risolte" value={stats.resolved} icon={CheckCircle2} color="bg-green-500/20" />
        <StatCard label="Urgenti" value={stats.urgent_count} icon={AlertCircle} color="bg-red-500/20" />
      </div>
      
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Cerca per oggetto, email, nome..."
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-[#121212] border border-[#2a2a2a] text-white placeholder-gray-600 focus:border-[#B08B5C] focus:outline-none"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-lg bg-[#121212] border border-[#2a2a2a] text-white focus:border-[#B08B5C] focus:outline-none"
          >
            <option value="">Tutti gli stati</option>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Requests list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="text-center py-12 bg-[#121212] rounded-xl border border-[#1a1a1a]">
          <MessageCircle className="w-12 h-12 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">Nessuna richiesta trovata</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredRequests.map((request) => {
            const statusConfig = STATUS_CONFIG[request.status];
            const priorityConfig = PRIORITY_CONFIG[request.priority];
            const StatusIcon = statusConfig.icon;
            
            return (
              <div
                key={request.id}
                onClick={() => fetchRequestDetail(request)}
                className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-4 cursor-pointer hover:border-[#B08B5C]/50 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg ${statusConfig.color}/20 flex items-center justify-center`}>
                      <StatusIcon className={`w-5 h-5 ${statusConfig.color.replace('bg-', 'text-')}`} />
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-white font-medium">{request.subject}</h4>
                        <span className={`text-xs ${priorityConfig.color}`}>
                          [{priorityConfig.label}]
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span>{request.user_email}</span>
                        <span>•</span>
                        <span>{CONTEXT_LABELS[request.context]}</span>
                        <span>•</span>
                        <span>{new Date(request.created_at).toLocaleDateString('it-IT')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color} text-white`}>
                      {statusConfig.label}
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Detail Modal */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          responses={responses}
          onClose={() => { setSelectedRequest(null); setResponses([]); }}
          onRespond={handleRespond}
          onUpdateStatus={handleUpdateStatus}
          adminId={adminUser?.id}
        />
      )}
    </div>
  );
};

export default AdminSupportPanel;
